/**
 * Analytics Event Definitions
 * Standardized event names and metadata schemas for tracking user actions
 */

// Event name constants
export const EVENTS = {
  // Prompt events
  PROMPT_VIEWED: 'prompt_viewed',
  PROMPT_CREATED: 'prompt_created',
  PROMPT_EDITED: 'prompt_edited',
  PROMPT_DELETED: 'prompt_deleted',
  PROMPT_FAVORITED: 'prompt_favorited',
  PROMPT_UNFAVORITED: 'prompt_unfavorited',
  PROMPT_UPVOTED: 'prompt_upvoted',
  PROMPT_DOWNVOTED: 'prompt_downvoted',
  PROMPT_COPIED: 'prompt_copied',
  
  // Search and filter events
  SEARCH_PERFORMED: 'search_performed',
  CATEGORY_FILTERED: 'category_filtered',
  
  // Weekly planner events
  WEEKLY_PLANNER_OPENED: 'weekly_planner_opened',
  WEEKLY_PLANNER_SAVED: 'weekly_planner_saved',
  
  // Authentication events
  USER_REGISTERED: 'user_registered',
  USER_LOGGED_IN: 'user_logged_in',
  
  // Navigation events
  PAGE_VIEWED: 'page_viewed',

  // Tutorial events
  TUTORIAL_STARTED: 'tutorial_started',
  TUTORIAL_STEP_COMPLETED: 'tutorial_step_completed',
  TUTORIAL_COMPLETED: 'tutorial_completed',
  TUTORIAL_SKIPPED: 'tutorial_skipped',
};

/**
 * Event metadata schemas
 * Defines required and optional properties for each event type
 */
export const EVENT_SCHEMAS = {
  [EVENTS.PROMPT_VIEWED]: {
    required: ['promptId'],
    optional: ['promptTitle', 'isGlobal', 'categories', 'source'],
  },
  [EVENTS.PROMPT_CREATED]: {
    required: ['promptId'],
    optional: ['promptTitle', 'categories', 'isGlobal'],
  },
  [EVENTS.PROMPT_EDITED]: {
    required: ['promptId'],
    optional: ['promptTitle', 'categories', 'isGlobal'],
  },
  [EVENTS.PROMPT_DELETED]: {
    required: ['promptId'],
    optional: ['promptTitle'],
  },
  [EVENTS.PROMPT_FAVORITED]: {
    required: ['promptId'],
    optional: ['promptTitle', 'isGlobal'],
  },
  [EVENTS.PROMPT_UNFAVORITED]: {
    required: ['promptId'],
    optional: ['promptTitle', 'isGlobal'],
  },
  [EVENTS.PROMPT_UPVOTED]: {
    required: ['promptId'],
    optional: ['promptTitle', 'isGlobal'],
  },
  [EVENTS.PROMPT_DOWNVOTED]: {
    required: ['promptId'],
    optional: ['promptTitle', 'isGlobal'],
  },
  [EVENTS.PROMPT_COPIED]: {
    required: ['promptId'],
    optional: ['promptTitle', 'isGlobal', 'source'],
  },
  [EVENTS.SEARCH_PERFORMED]: {
    required: ['query'],
    optional: ['resultCount', 'selectedCategories'],
  },
  [EVENTS.CATEGORY_FILTERED]: {
    required: ['categories'],
    optional: ['selectedCount'],
  },
  [EVENTS.WEEKLY_PLANNER_OPENED]: {
    required: [],
    optional: ['trigger', 'hasExistingPrompts'],
  },
  [EVENTS.WEEKLY_PLANNER_SAVED]: {
    required: ['promptCount'],
    optional: ['activityCount', 'selectedActivities'],
  },
  [EVENTS.USER_REGISTERED]: {
    required: ['userId'],
    optional: ['orgId'],
  },
  [EVENTS.USER_LOGGED_IN]: {
    required: ['userId'],
    optional: ['orgId'],
  },
  [EVENTS.PAGE_VIEWED]: {
    required: ['path'],
    optional: ['component', 'searchParams'],
  },
  [EVENTS.TUTORIAL_STARTED]: {
    required: [],
    optional: ['userId'],
  },
  [EVENTS.TUTORIAL_STEP_COMPLETED]: {
    required: ['stepId', 'stepIndex'],
    optional: ['totalSteps'],
  },
  [EVENTS.TUTORIAL_COMPLETED]: {
    required: [],
    optional: ['totalSteps'],
  },
  [EVENTS.TUTORIAL_SKIPPED]: {
    required: ['stepId', 'stepIndex'],
    optional: ['totalSteps'],
  },
};

/**
 * Validate event properties against schema
 * @param {string} eventName - The event name
 * @param {Object} properties - Event properties
 * @returns {Object} - { valid: boolean, errors: string[] }
 */
export function validateEvent(eventName, properties) {
  const schema = EVENT_SCHEMAS[eventName];
  if (!schema) {
    return { valid: false, errors: [`Unknown event: ${eventName}`] };
  }
  
  const errors = [];
  
  // Check required properties
  for (const required of schema.required) {
    if (properties[required] === undefined || properties[required] === null) {
      errors.push(`Missing required property: ${required}`);
    }
  }
  
  return {
    valid: errors.length === 0,
    errors,
  };
}

